import { memo, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface RipplesProps {
  size?: number | string;
  speed?: number | string;
  className?: string;
}

const readPrimary = () => {
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue("--primary")
    .trim();
  return v ? `hsl(${v})` : "hsl(160 84% 39%)";
};

/**
 * Themed wrapper around the ldrs <l-ripples> web component.
 * Reads --primary once and only recomputes when the `dark` class
 * on <html> actually toggles, to avoid restarting the SVG animation
 * on unrelated attribute mutations.
 */
const RipplesInner = ({ size = 45, speed = 1.75, className }: RipplesProps) => {
  const [color, setColor] = useState(() =>
    typeof window === "undefined" ? "hsl(160 84% 39%)" : readPrimary(),
  );

  useEffect(() => {
    let isDark = document.documentElement.classList.contains("dark");
    const mo = new MutationObserver(() => {
      const nowDark = document.documentElement.classList.contains("dark");
      if (nowDark === isDark) return;
      isDark = nowDark;
      const next = readPrimary();
      setColor((prev) => (prev === next ? prev : next));
    });
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => mo.disconnect();
  }, []);

  return (
    <div
      className={cn("flex items-center justify-center", className)}
      style={{ contain: "layout paint", willChange: "transform" }}
    >
      <l-ripples size={String(size)} speed={String(speed)} color={color} />
    </div>
  );
};

export const Ripples = memo(RipplesInner);

export default Ripples;